/**
 * Structured Logging Utility
 * Outputs logs to stdout/stderr which Azure Web Apps automatically captures
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  [key: string]: unknown;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  service: string;
}

class Logger {
  private serviceName = process.env.SERVICE_NAME || "rc-web";
  
  // Log levels: debug < info < warn < error
  private logLevels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  // Get NODE_ENV dynamically (not cached) to respect Azure runtime environment variables
  private getNodeEnv(): string {
    return process.env.NODE_ENV || "production";
  }

  private isDevelopment(): boolean {
    return this.getNodeEnv() === "development";
  }

  // Get minimum log level from environment variable
  // Debug level is only available in development mode
  private getMinLogLevel(): LogLevel {
    const envLogLevel = process.env.LOG_LEVEL?.toLowerCase() as LogLevel | undefined;
    
    // If LOG_LEVEL is set and valid
    if (envLogLevel && this.logLevels[envLogLevel] !== undefined) {
      // Debug is only allowed in development - ignore if set in production
      if (envLogLevel === "debug" && !this.isDevelopment()) {
        return "info"; // Default to info in production even if debug is requested
      }
      return envLogLevel;
    }
    
    // Default: debug in development, info in production
    return this.isDevelopment() ? "debug" : "info";
  }

  private shouldLog(level: LogLevel): boolean {
    // Debug logs are only allowed in development mode
    if (level === "debug" && !this.isDevelopment()) {
      return false;
    }
    return this.logLevels[level] >= this.logLevels[this.getMinLogLevel()];
  }

  /* An Error keeps message and stack as non-enumerable properties, so JSON.stringify
     flattens a logged one to {} and the Azure record carries a failure with nothing in
     it. Pull them out by hand, over a spread of whatever the subclass declared on top --
     status, code and data on an ApiError. */
  private unwrapErrors(context?: LogContext): LogContext | undefined {
    if (!context) return context;

    let found = false;
    const unwrapped: LogContext = {};

    for (const [key, value] of Object.entries(context)) {
      if (value instanceof Error) {
        found = true;
        unwrapped[key] = {
          ...value,
          name: value.name,
          message: value.message,
          stack: value.stack,
        };
      } else {
        unwrapped[key] = value;
      }
    }

    return found ? unwrapped : context;
  }

  private log(level: LogLevel, message: string, context?: LogContext) {
    if (!this.shouldLog(level)) {
      return;
    }

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: this.unwrapErrors(context),
      service: this.serviceName,
    };

    // In development, use pretty-printed console output for better readability
    if (this.isDevelopment()) {
      const consoleMethod = level === "error" ? console.error : level === "warn" ? console.warn : console.log;
      consoleMethod(`[${level.toUpperCase()}] ${logEntry.timestamp} - ${message}`, logEntry.context || "");
      return;
    }

    // In production (Azure), output structured JSON to stdout/stderr
    // Azure Web Apps automatically captures stdout/stderr and displays them in Log Stream
    // JSON format allows for better parsing and querying in Azure Application Insights
    const logOutput = JSON.stringify(logEntry);

    // Use appropriate stream based on log level
    // Errors and warnings go to stderr, info and debug go to stdout
    // This helps Azure categorize and filter logs appropriately
    if (level === "error" || level === "warn") {
      // stderr - errors and warnings
      console.error(logOutput);
    } else {
      // stdout - info and debug
      console.log(logOutput);
    }
  }

  debug(message: string, context?: LogContext) {
    // Debug logs are only available in development mode
    if (!this.isDevelopment()) {
      return;
    }
    this.log("debug", message, context);
  }

  info(message: string, context?: LogContext) {
    this.log("info", message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log("warn", message, context);
  }

  error(message: string, context?: LogContext) {
    this.log("error", message, context);
  }
}

export const logger = new Logger();